import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RotateCw } from 'lucide-react';
import type { CaptchaTracePoint } from '../../types';
import { CaptchaViewProps, pngSrc, toImagePoint } from './captchaUtils';
import { CaptchaHelp } from './CaptchaHelp';

const TRACK_UNITS = 360;

/** Turn the picture upright with the slider, then confirm. */
export function RotateUprightCaptcha({ challenge, startedAt, disabled, onSubmit }: CaptchaViewProps) {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const trace = useRef<CaptchaTracePoint[]>([]);
  const [angle, setAngle] = useState(0);
  const [dragging, setDragging] = useState(false);

  // The track is measured in degrees (0..360) so the trace x axis is the angle.
  const angleFor = (event: React.PointerEvent) => {
    const point = toImagePoint(event, trackRef.current!, TRACK_UNITS, startedAt);
    const value = Math.min(TRACK_UNITS, Math.max(0, point.x));
    return { point: { ...point, x: value }, value };
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const { point, value } = angleFor(event);
    trace.current.push(point);
    setAngle(value);
    setDragging(true);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    const { point, value } = angleFor(event);
    trace.current.push(point);
    setAngle(value);
  };

  const percent = `${(angle / TRACK_UNITS) * 100}%`;

  return (
    <div className="captcha-rotate">
      <CaptchaHelp type="rotate" />
      <div className="captcha-rotate-frame">
        <img
          className="captcha-rotate-image"
          src={pngSrc(challenge.image)}
          alt=""
          draggable={false}
          style={{ transform: `rotate(${angle}deg)` }}
        />
      </div>
      <div
        ref={trackRef}
        className={`captcha-track ${dragging ? 'captcha-track-active' : ''}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={() => setDragging(false)}
        onPointerCancel={() => setDragging(false)}
        role="slider"
        aria-label={t('captcha.rotate.instruction')}
        aria-valuemin={0}
        aria-valuemax={360}
        aria-valuenow={Math.round(angle)}
      >
        <div className="captcha-track-fill" style={{ width: percent }} />
        <div className="captcha-handle captcha-handle-round" style={{ left: `calc(${percent} - 20px)` }}>
          <RotateCw size={18} />
        </div>
      </div>
      <button
        type="button"
        className="btn-modern btn-primary-modern captcha-confirm"
        disabled={disabled || trace.current.length === 0}
        onClick={() => onSubmit({ angle, trace: trace.current })}
      >
        {t('captcha.confirm')}
      </button>
    </div>
  );
}
